import { API_BASE_URL } from '@/utils/config';
import type { AnalyticsEvent, DeviceInfo, DeviceTier } from '@/types';

const BATCH_SIZE = 10;
const FLUSH_INTERVAL = 5000;

let eventQueue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let sessionId: string | null = null;

export function getSessionId(): string {
  if (!sessionId) {
    sessionId = crypto.randomUUID();
  }
  return sessionId;
}

function getDeviceInfo(): DeviceInfo {
  const hasWebGPU = typeof navigator !== 'undefined' && 'gpu' in navigator;

  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let os = 'Unknown';

  if (ua.includes('Chrome')) browser = 'Chrome';
  else if (ua.includes('Safari')) browser = 'Safari';
  else if (ua.includes('Firefox')) browser = 'Firefox';
  else if (ua.includes('Edge')) browser = 'Edge';

  if (ua.includes('Win')) os = 'Windows';
  else if (ua.includes('Mac')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  // @ts-expect-error deviceMemory is non-standard
  const ram = navigator.deviceMemory || 4;
  const tier: DeviceTier = hasWebGPU && ram >= 4 ? 'A' : ram >= 3 ? 'B' : 'C';

  return {
    browser,
    os,
    deviceTier: tier,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    webgpuSupported: hasWebGPU,
  };
}

export function trackEvent(
  eventType: string,
  properties?: Record<string, unknown>,
  finishId?: string
) {
  const event: AnalyticsEvent = {
    session_id: getSessionId(),
    event_type: eventType,
    finish_id: finishId,
    properties,
    device_info: getDeviceInfo(),
    timestamp: new Date().toISOString(),
  };

  eventQueue.push(event);

  if (eventQueue.length >= BATCH_SIZE) {
    flushEvents();
  } else {
    scheduleFlush();
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushEvents();
  }, FLUSH_INTERVAL);
}

async function flushEvents() {
  if (eventQueue.length === 0) return;

  const events = [...eventQueue];
  eventQueue = [];

  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }

  try {
    // Cache-bust to prevent browser from caching old failed preflight responses
    const cb = `?_cb=${Date.now()}`;
    if (events.length === 1) {
      await fetch(`${API_BASE_URL}/api/data/event${cb}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(events[0]),
        keepalive: true,
        cache: 'no-store',
      });
    } else {
      await fetch(`${API_BASE_URL}/api/data/batch${cb}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events }),
        keepalive: true,
        cache: 'no-store',
      });
    }
  } catch (err) {
    // Silently fail — analytics should never block UX.
    // Do NOT retry: CORS errors, ad-blocker blocks, and network failures
    // are persistent per session. Retrying just creates noise in the console
    // and network tab. Drop events immediately.
    if (import.meta.env.DEV) {
      console.warn('Analytics flush failed (dropped):', err);
    }
  }
}

// Flush on page unload — prefer sendBeacon for reliability
window.addEventListener('beforeunload', () => {
  if (eventQueue.length === 0) return;

  const events = [...eventQueue];
  eventQueue = [];
  const body = JSON.stringify(events.length === 1 ? events[0] : { events });

  const url =
    events.length === 1
      ? `${API_BASE_URL}/api/data/event`
      : `${API_BASE_URL}/api/data/batch`;

  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    navigator.sendBeacon(url, body);
  } else {
    // Fallback: fire-and-forget fetch with keepalive
    fetch(url, { method: 'POST', body, keepalive: true }).catch(() => {});
  }
});
